import superagent from 'superagent';
import Mock from '@utils/mock';
import API from '../config';

/*
   When the client app intercepts a Clareity redirection from the API (see [responseBody] function), we can instruct Clareity to redirect the user to a specific page (by default to the page they last were)
   once this finishes processing the auth request, and we are doing this by setting the [returnUrl] query parameter.

   Clareity currently gives an "Access denied / Error 15" error when passing the return URL as an absolute URL based on "localhost" domain (e.g. ?returnUrl=http://localhost:3000/listings/view?ListingID=A2010754).
   Exception is the case where the return URL is actually the base URL (e.g. ?returnUrl=http://localhost:3000/). The redirection is successful in this case.
   The Clareity redirection also seems to be successful when using absolute URLs based on "pillarnine.com" domain (e.g. ?returnUrl=https://dev-oneplatform.pillarnine.com/listings/view?ListingID=A1061795).

   Given the redirection issues, we can choose to disable the returnUrl, when testing locally, via the [useReturnUrl] parameter - disabling will result in always redirecting to the [AuthSuccessRedirect] URL
   defined on the API side (currently set to http://localhost:3000 for dev environment).
*/
const useReturnUrl = true;

/*
   Use [lastViewedEntity] to keep track in the current module of the last viewed entity, in order to calculate the return URL when session expiration occurs
   during the entity edit - the return URL in this case should be set to the View page of the entity being edited.
*/
let lastViewedEntity = null;
export const saveLastViewedEntityToApiService = entity => {
  lastViewedEntity = entity;
};

const getRedirectionUrl = () => {
  const currentUrl = window.location.href;
  if (currentUrl.indexOf('/edit') > -1) {
    // Instruct Clareity to redirect the user to the View page instead.
    const baseUrl = window.location.origin;
    switch (lastViewedEntity?.type) {
      case 'listing':
        return encodeURI(`${baseUrl}/listings/view?ListingID=${lastViewedEntity.id}`);
      case 'agent':
        return encodeURI(`${baseUrl}/agent/view?UniqueID=${lastViewedEntity.id}`);
      case 'office':
        return encodeURI(`${baseUrl}/office/view?UniqueID=${lastViewedEntity.id}`);
      default:
        return '';
    }
  }
  return encodeURI(currentUrl);
};

const responseBody = async ({ body }) => {
  if (body.ClareityUrl) {
    if (body.ClareityUrl.includes('logout')) {
      window.location.href = body.ClareityUrl;
      return null;
    }
    const { body: loginBody } = await superagent.get(`${API.URL}auth/Clareity${useReturnUrl ? `?returnUrl=${getRedirectionUrl()}` : ''}`);
    window.location.href = loginBody?.ClareityUrl;
    return null;
  }
  return body;
};

/* DO NOT USE THIS REQUEST. ONLY FOR THE CLAREITY LOGIN */
const NoTokenRequests = {
  Get: url => superagent.get(`${API.URL}${url}`).then(responseBody),
};

const tokenPlugin = req => {
  req.withCredentials()
    .set('Accept', 'application/json')
    .set('Access-Control-Allow-Origin', API.URL)
    .set('Access-Control-Allow-Credentials', true);
};

const Requests = {
  Delete: url => superagent.del(`${API.URL}${url}`).use(tokenPlugin).then(responseBody),
  Get: url => superagent.get(`${API.URL}${url}`).use(tokenPlugin).then(responseBody),
  GetBlob: url => superagent.get(`${API.URL}${url}`).use(tokenPlugin).responseType('blob').then(responseBody),
  Post: (url, body) => superagent.post(`${API.URL}${url}`, body).use(tokenPlugin).then(responseBody),
  PostBlob: (url, body) => superagent.post(`${API.URL}${url}`, body).use(tokenPlugin).responseType('blob').then(responseBody),
  Put: (url, body) => superagent.put(`${API.URL}${url}`, body).use(tokenPlugin).then(responseBody),
};

const Acl = {
  GetAclDetails: (resourceType, requestDetails) => Requests.Post(`acl/${resourceType}`, requestDetails),
};

const Agent = {
  agentLookup: agentData => Requests.Post('agents/AgentLookup', { ...agentData }),
  createAgent: agentData => Requests.Post('agents/insert', { ...agentData }),
  getCurrentAgent: () => Requests.Get('agents/userFill'),
  searchAgent: agentCriteria => Requests.Post('agents/search', { ...agentCriteria }),
  selectAgent: agentData => Requests.Post('agents/select', { ...agentData }),
  updateAgent: agentData => Requests.Post('agents/update', { ...agentData }),
};

const AuditLog = {
  GetAuditLog: (className, tableName, uniqueID, entityID) => Requests.Post('AuditLog/query', className, tableName, uniqueID, entityID),
};

const Auth = {
  Authenticate: () => Requests.Get('noauth'),
  ClareityLogin: () => NoTokenRequests.Get('auth/Clareity'),
  Heartbeat: () => Requests.Get('auth/heartbeat'),
  Status: () => Requests.Get('auth/Status'),
  Logout: () => Requests.Get('auth/Logout'),
};

const AutoSave = {
  autoSave: saveData => Requests.Post('autosave/create', { ...saveData }),
  checkAutoSave: autoSaveCriteria => Requests.Post('autosave/check', { ...autoSaveCriteria }),
  getAutoSaveData: autoSaveCriteria => Requests.Post('autosave/retrieve', { ...autoSaveCriteria }),
  removeAutoSaveData: autoSaveCriteria => Requests.Post('autosave/remove', { ...autoSaveCriteria }),
  updateAutoSaveData: saveData => Requests.Post('autosave/updateData', { ...saveData }),
};

const Condo = {
  createCondoName: condoData => Requests.Post('CondoName/insert', { ...condoData }),
  searchCondoName: searchCriteria => Requests.Post('CondoName/search', { ...searchCriteria }),
  selectCondoName: UniqueID => Requests.Post('CondoName/select', { ...UniqueID }), // unused at the moment
  updateCondoName: condoRecord => Requests.Post('CondoName/update', { ...condoRecord }),
};

const Definitions = {
  getFormDefinition: formType => Requests.Get(`definition/${formType}`),
  GetMockForm: () => Requests.Get('Definition/LayoutMock'),
  PostToLookupTable: lookupNames => Requests.Post('definition/lookup', { ...lookupNames }),
  getProfileDefinition: () => Mock.Get('definition/profile', { response: { id: 1 } }),
};

const Impersonation = {
  Start: imposterID => Requests.Post('Impersonation/Start', { ...imposterID }),
  Stop: imposterID => Requests.Get('Impersonation/Stop', { ...imposterID }),
  GetStatus: () => Requests.Get('Impersonation/Status'),
  AddAllowedUser: userInfo => Requests.Post('Impersonation/AddAllowedUser', { ...userInfo }),
  RemoveAllowedUser: userInfo => Requests.Post('Impersonation/RemoveAllowedUser', { ...userInfo }),
  AddImposter: userInfo => Requests.Post('Impersonation/AddImposter', { ...userInfo }),
  RemoveImposter: userInfo => Requests.Post('Impersonation/RemoveImposter', { ...userInfo }),
  GetImpersonationData: () => Requests.Get('Impersonation/GetImpersonationData '),
};

const Listing = {
  CreateListing: request => Requests.Post('Product/Listings/save', { ...request }),
  UpdateListing: request => Requests.Post('Product/Listings/update', { ...request }),
  DeleteListingById: listingData => Requests.Post('Product/Listings/delete', { ...listingData }),
  CopyListing: searchListingCriteria => Requests.Post('Product/Listings/copy', { ...searchListingCriteria }),
  GetMyListings: () => Requests.Get('Product/Listings/myeditablelistings'),
  GetListingById: searchListingCriteria => Requests.Post('Product/Listings/record', { ...searchListingCriteria }),
  GetAllListings: () => Requests.Get('Product/Listings'),
  TaxAutoPopSearch: taxData => Requests.Post('taxpop/search/2', { ...taxData }),
  TaxAutoPopSelect: taxData => Requests.Post('taxpop/select/2', { ...taxData }),
  SearchListings: searchListingCriteria => Requests.Post('Product/Listings/f', { ...searchListingCriteria }),
  SaveSupplement: form => Requests.Post('Supplement', form),
  SaveIncomplete: request => Requests.Post('Product/Listings/save/inc', { ...request }),
  UpdateIncomplete: request => Requests.Post('Product/Listings/update/incomplete', { ...request }),
};

const Office = {
  getOfficeDefinition: () => Requests.Get('definition/offices'),
  insertOffice: officeData => Requests.Post('profile/offices/insert', { ...officeData }),
  officeLookup: searchCriteria => Requests.Post('profile/offices/OfficeLookup', { ...searchCriteria }),
  searchOffices: searchCriteria => Requests.Post('profile/offices/search', { ...searchCriteria }),
  selectOffice: officeId => Requests.Post('profile/offices/select', { ...officeId }),
  updateOffice: officeData => Requests.Post('profile/offices/update', { ...officeData }),
};

const Photos = {
  getPreferredPhoto: uniqueID => Requests.Post('Image/GetPreferredPhoto', { key: uniqueID }),
  getAllImages: files => Requests.PostBlob('Image/GetAwsFilesAsZip', files),
  getSingleImage: file => Requests.GetBlob(`Image/GetAwsFile?file=${file}`),
  postImage: form => Requests.Post('image', form),
};

const RowLock = {
  CheckRowLock: entity => Requests.Post('RowLock/check', entity),
  CreateRowLock: entity => Requests.Post('RowLock/create', entity),
  PingRowLock: entity => Requests.Post('RowLock/ping', entity),
  RemoveRowLock: entity => Requests.Post('RowLock/remove', entity),
  TakeoverRowLock: entity => Requests.Post('RowLock/takeover', entity),
};

const Team = {
  getActors: () => Requests.Get('authdemo/actors'),
  getTargets: () => Requests.Get('authdemo/targets'),
  getListOfTeam: () => Requests.Get('authdemo'),
};

const TeamsManagement = {
  SearchMember: member => Requests.Post('teams/teamMemberSearch', { ...member }),
  AddMember: member => Requests.Post('teams/addToTeam', { ...member }),
  RemoveMember: member => Requests.Post('teams/removeFromTeam', { ...member }),
};

const User = {
  getUserName: () => Requests.Get('user/name'),
  getUser: () => Requests.Get('user'),
  updateUser: newData => Requests.Put('user', { data: { ...newData } }),
};

export default {
  Acl,
  AuditLog,
  Agent,
  Auth,
  AutoSave,
  Condo,
  Definitions,
  Impersonation,
  Listing,
  Office,
  Photos,
  RowLock,
  Team,
  TeamsManagement,
  User,
};
